import React, { useState } from 'react';
import {
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  ShoppingCart as CartIcon,
} from '@mui/icons-material';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { cartService } from '../../services/cartService';

interface CheckoutModalProps {
  open: boolean;
  onClose: () => void;
  onCheckoutSuccess?: () => void;
  projectId: string;
  projectName: string;
  clientName: string;
  planId?: string;
}

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  open,
  onClose,
  onCheckoutSuccess,
  projectId,
  projectName,
  clientName,
  planId,
}) => {
  const { cart, loading, cartCount, cartTotal, fetchCart, checkout } = useCart(projectId);
  const { isClient, isAgency } = useAuth();
  const usesPaymentCheckout = isClient || isAgency;
  const hasFetchedRef = React.useRef(false);

  // Fetch cart when modal opens to ensure fresh data
  React.useEffect(() => {
    if (open && projectId && !hasFetchedRef.current) {
      fetchCart();
      hasFetchedRef.current = true;
    }
    
    // Reset the ref when modal closes
    if (!open) {
      hasFetchedRef.current = false;
    }
  }, [open, projectId, fetchCart]);

  const currentDate = new Date();
  const [orderMonth, setOrderMonth] = useState(currentDate.getMonth() + 1);
  const [orderYear, setOrderYear] = useState(currentDate.getFullYear());
  const [notes, setNotes] = useState('');
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [placementsCreated, setPlacementsCreated] = useState(0);
  // Tracks the in-flight checkout (Stripe session creation + redirect, or admin
  // direct checkout) so the button shows a loader and can't be clicked twice.
  const [processing, setProcessing] = useState(false);

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() + i);

  const handleCheckout = async () => {
    if (processing) return;
    setCheckoutError(null);
    setCheckoutSuccess(false);
    setProcessing(true);

    try {
      // Client and agency roles use Stripe payment flow
      if (usesPaymentCheckout) {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('paymentStatus', 'success');
        const successUrl = currentUrl.toString();

        const cancelUrl = window.location.href;
        const response = await cartService.createStripeCheckoutSession({
          projectId,
          orderMonth,
          orderYear,
          notes,
          planId,
          successUrl,
          cancelUrl,
        });

        if (!response?.data?.checkoutUrl) {
          throw new Error('Stripe checkout URL was not returned');
        }

        // Keep `processing` true through the redirect so the button stays
        // disabled and the user can't trigger a second Stripe session.
        window.location.href = response.data.checkoutUrl;
        return;
      }

      // Admin/Super Admin use direct checkout (no payment gateway)
      const response = await checkout(orderMonth, orderYear, notes, planId);
      setCheckoutSuccess(true);
      setPlacementsCreated(response?.data?.placementsCreated || 0);
      await fetchCart();
      onCheckoutSuccess?.();

      setTimeout(() => {
        onClose();
      }, 1200);
      return;
    } catch (error: any) {
      setCheckoutError(error.response?.data?.error || error.message || 'Failed to start checkout');
      setProcessing(false);
    }
  };

  const handleClose = () => {
    if (!loading && !processing && !checkoutSuccess) {
      setCheckoutError(null);
      setCheckoutSuccess(false);
      setNotes('');
      onClose();
    }
  };

  const budgetUsedPercent = cart?.budgetUsedPercent || 0;
  const budgetRemaining = cart?.budgetRemaining || 0;
  const projectBudget = cart?.projectBudget || 0;
  const clientBudget = cart?.clientBudget || 0;
  const monthlySpent = cart?.monthlySpent;
  const monthPlacementSpent = cart?.monthPlacementSpent || 0;
  const monthOrderSpent = cart?.monthOrderSpent || 0;

  return (
    <>
      <style>{`
        .checkout-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1300;
          display: ${open ? 'flex' : 'none'};
          align-items: center;
          justify-content: center;
          padding: 20px;
        }
        .checkout-modal {
          background: white;
          border-radius: 12px;
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          border: 1px solid #e5e7eb;
        }
        .checkout-modal-header {
          background: #f9fafb;
          padding: 20px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid #e5e7eb;
        }
        .checkout-modal-title {
          display: flex;
          align-items: center;
          gap: 12px;
          color: #1f2937;
          font-size: 20px;
          font-weight: 600;
        }
        .checkout-modal-title svg {
          width: 24px;
          height: 24px;
          color: #3b82f6;
        }
        .checkout-modal-close {
          padding: 8px;
          border: none;
          background: #f3f4f6;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.15s;
          color: #6b7280;
        }
        .checkout-modal-close:hover {
          background: #e5e7eb;
          color: #374151;
        }
        .checkout-modal-close svg {
          width: 20px;
          height: 20px;
        }
        .checkout-modal-content {
          padding: 24px;
          overflow-y: auto;
          max-height: calc(90vh - 160px);
        }
        .checkout-success {
          text-align: center;
          padding: 60px 20px;
        }
        .checkout-success svg {
          width: 64px;
          height: 64px;
          color: #10B981;
          margin-bottom: 16px;
        }
        .checkout-success h5 {
          color: #10B981;
          font-size: 24px;
          font-weight: 600;
          margin-bottom: 8px;
        }
        .checkout-success p {
          color: #64748B;
          font-size: 14px;
          margin-bottom: 4px;
        }
        .checkout-info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 20px;
        }
        .checkout-info-card {
          background: #F8FAFC;
          border-radius: 8px;
          padding: 16px;
          height: 100%;
        }
        .checkout-info-label {
          font-size: 12px;
          color: #64748B;
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .checkout-info-value {
          font-size: 15px;
          font-weight: 600;
          color: #1E293B;
          margin-bottom: 8px;
        }
        .checkout-info-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 4px;
        }
        .checkout-info-row-label {
          font-size: 12px;
          color: #64748B;
        }
        .checkout-info-row-value {
          font-size: 12px;
          font-weight: 600;
          color: #1E293B;
        }
        .checkout-info-row-value.primary {
          color: #2563EB;
        }
        .checkout-info-row-value.success {
          color: #10B981;
        }
        .checkout-info-row-value.error {
          color: #EF4444;
        }
        .checkout-warning {
          background: #FEF3C7;
          border: 1px solid #F59E0B;
          border-radius: 8px;
          padding: 12px 16px;
          margin-bottom: 20px;
          font-size: 13px;
          color: #92400E;
        }
        .checkout-divider {
          height: 1px;
          background: #E2E8F0;
          margin: 20px 0;
        }
        .checkout-section-title {
          font-size: 16px;
          font-weight: 600;
          color: #1E293B;
          margin-bottom: 12px;
        }
        .checkout-loading {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 40px;
          gap: 16px;
        }
        .checkout-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #E2E8F0;
          border-top-color: #2563EB;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .checkout-empty {
          text-align: center;
          padding: 40px;
          background: #F8FAFC;
          border-radius: 8px;
        }
        .checkout-empty svg {
          width: 48px;
          height: 48px;
          color: #94A3B8;
          margin-bottom: 8px;
        }
        .checkout-empty p {
          color: #64748B;
          font-size: 14px;
        }
        .checkout-items-grid {
          max-height: 300px;
          overflow-y: auto;
          background: #F8FAFC;
          border-radius: 8px;
          padding: 8px;
        }
        .checkout-items-grid-inner {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }
        .checkout-item-card {
          background: white;
          border: 1px solid #E2E8F0;
          border-radius: 8px;
          padding: 12px;
        }
        .checkout-item-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 6px;
        }
        .checkout-item-url {
          font-size: 12px;
          font-weight: 600;
          color: #1E293B;
          flex: 1;
          margin-right: 8px;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          line-height: 1.3;
        }
        .checkout-item-price {
          font-size: 12px;
          font-weight: 700;
          color: #2563EB;
          flex-shrink: 0;
        }
        .checkout-item-chips {
          display: flex;
          gap: 4px;
        }
        .checkout-item-chip {
          padding: 2px 6px;
          background: #F1F5F9;
          color: #475569;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 500;
        }
        .checkout-form-group {
          margin-bottom: 20px;
        }
        .checkout-form-row {
          display: flex;
          gap: 16px;
          margin-bottom: 16px;
        }
        .checkout-form-field {
          flex: 1;
        }
        .checkout-label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          margin-bottom: 6px;
        }
        .checkout-input, .checkout-select, .checkout-textarea {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #D1D5DB;
          border-radius: 6px;
          font-size: 14px;
          font-family: inherit;
          transition: all 0.15s;
        }
        .checkout-input:focus, .checkout-select:focus, .checkout-textarea:focus {
          outline: none;
          border-color: #2563EB;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }
        .checkout-textarea {
          resize: vertical;
          min-height: 80px;
        }
        .checkout-error {
          background: #FEF2F2;
          border: 1px solid #EF4444;
          border-radius: 8px;
          padding: 12px 16px;
          margin-bottom: 20px;
          font-size: 13px;
          color: #991B1B;
        }
        .checkout-modal-footer {
          background: #f9fafb;
          padding: 16px 24px;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          border-top: 1px solid #e5e7eb;
        }
        .checkout-btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          border: none;
          font-family: inherit;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .checkout-btn svg {
          width: 16px;
          height: 16px;
        }
        .checkout-btn-secondary {
          background: white;
          color: #475569;
          border: 1px solid #E2E8F0;
        }
        .checkout-btn-secondary:hover {
          background: #F8FAFC;
        }
        .checkout-btn-primary {
          background: #2563EB;
          color: white;
        }
        .checkout-btn-primary:hover {
          background: #1D4ED8;
        }
        .checkout-btn-primary:disabled {
          background: #94A3B8;
          cursor: not-allowed;
        }
      `}</style>
      <div className="checkout-modal-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
        <div className="checkout-modal" onClick={(e) => e.stopPropagation()}>
          <div className="checkout-modal-header">
            <div className="checkout-modal-title">
              <CartIcon />
              <span>Checkout</span>
            </div>
            <button className="checkout-modal-close" onClick={handleClose} disabled={loading || checkoutSuccess}>
              <CloseIcon />
            </button>
          </div>

          <div className="checkout-modal-content">
            {checkoutSuccess ? (
              <div className="checkout-success">
                <CheckIcon />
                <h5>Checkout Successful!</h5>
                <p>{placementsCreated} placement{placementsCreated !== 1 ? 's' : ''} created successfully.</p>
                <p>Redirecting to placements page...</p>
              </div>
        ) : processing ? (
          /* Stable loader while the checkout request runs. The cart context
             empties mid-request, which used to flash blank/"No items in cart"
             before the success state rendered. */
          <div className="checkout-loading" style={{ padding: '48px 0', justifyContent: 'center' }}>
            <div className="checkout-spinner" />
            <span>{usesPaymentCheckout ? 'Redirecting to payment…' : 'Creating placements…'}</span>
          </div>
        ) : (
          <>
              {/* Compact Header: Client, Project & Budget Summary in One Row */}
              <div className="checkout-info-grid">
                {/* Left: Client & Project */}
                <div className="checkout-info-card">
                  <div className="checkout-info-label">Client</div>
                  <div className="checkout-info-value">{clientName}</div>
                  <div className="checkout-info-label">Project</div>
                  <div className="checkout-info-value">{projectName}</div>
                </div>
                
                {/* Right: Budget Summary */}
                <div className="checkout-info-card">
                  <div className="checkout-info-label">Monthly Budget Summary</div>
                  <div className="checkout-info-row">
                    <span className="checkout-info-row-label">Monthly Budget:</span>
                    <span className="checkout-info-row-value">${projectBudget.toFixed(0)}/mo</span>
                  </div>
                  {monthlySpent !== undefined && (
                    <>
                      <div className="checkout-info-row">
                        <span className="checkout-info-row-label">Placements this month:</span>
                        <span className="checkout-info-row-value">${monthPlacementSpent.toFixed(0)}</span>
                      </div>
                      <div className="checkout-info-row">
                        <span className="checkout-info-row-label">Orders this month:</span>
                        <span className="checkout-info-row-value">${monthOrderSpent.toFixed(0)}</span>
                      </div>
                    </>
                  )}
                  <div className="checkout-info-row">
                    <span className="checkout-info-row-label">Cart:</span>
                    <span className="checkout-info-row-value primary">${cartTotal.toFixed(0)}</span>
                  </div>
                  <div className="checkout-info-row">
                    <span className="checkout-info-row-label">{budgetRemaining < 0 ? 'Over Budget:' : 'Remaining this month:'}</span>
                    <span className={`checkout-info-row-value ${budgetRemaining < 0 ? 'error' : 'success'}`}>
                      {budgetRemaining < 0 ? '-' : ''}${Math.abs(budgetRemaining).toFixed(0)}
                    </span>
                  </div>
                </div>
              </div>
            
              {budgetUsedPercent >= 100 && (
                <div className="checkout-warning">
                  Warning: This checkout will exceed the monthly project budget.
                </div>
              )}

            <div className="checkout-divider" />

              {/* Cart Items - 2 Column Layout */}
              <div className="checkout-form-group">
                <div className="checkout-section-title">Cart Items ({cartCount})</div>
                {loading && !cart ? (
                  <div className="checkout-loading">
                    <div className="checkout-spinner" />
                    <span>Loading cart items...</span>
                  </div>
                ) : cartCount === 0 ? (
                  <div className="checkout-empty">
                    <CartIcon />
                    <p>No items in cart</p>
                  </div>
                ) : (
                  <div className="checkout-items-grid">
                    <div className="checkout-items-grid-inner">
                      {cart?.cartItems.map((item) => (
                        <div key={item.id} className="checkout-item-card">
                          <div className="checkout-item-header">
                            <div className="checkout-item-url">{item.siteUrl}</div>
                            <div className="checkout-item-price">${item.price.toFixed(0)}</div>
                          </div>
                          <div className="checkout-item-chips">
                            <span className="checkout-item-chip">DA: {item.siteDa}</span>
                            <span className="checkout-item-chip">DR: {item.siteDr}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

            <div className="checkout-divider" />

              {/* Order Details */}
              <div className="checkout-form-group">
                <div className="checkout-section-title">Order Details</div>
                {/* Month/year are auto-captured from the current date for clients;
                    only admins can override them manually. */}
                {!usesPaymentCheckout && (
                  <div className="checkout-form-row">
                    <div className="checkout-form-field">
                      <label className="checkout-label">Order Month</label>
                      <select 
                        className="checkout-select"
                        value={orderMonth}
                        onChange={(e) => setOrderMonth(Number(e.target.value))}
                        disabled={loading}
                      >
                        {months.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="checkout-form-field">
                      <label className="checkout-label">Order Year</label>
                      <select 
                        className="checkout-select"
                        value={orderYear}
                        onChange={(e) => setOrderYear(Number(e.target.value))}
                        disabled={loading}
                      >
                        {years.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                <div className="checkout-form-field">
                  <label className="checkout-label">Notes (Optional)</label>
                  <textarea
                    className="checkout-textarea"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={loading}
                    placeholder="Add any notes for these placements..."
                  />
                </div>
              </div>

              {/* Error Message */}
              {checkoutError && (
                <div className="checkout-error">
                  {checkoutError}
                </div>
              )}
          </>
        )}
            </div>

          {!checkoutSuccess && (
            <div className="checkout-modal-footer">
              <button
                className="checkout-btn checkout-btn-secondary"
                onClick={handleClose}
                disabled={loading || processing}
              >
                Cancel
              </button>
              <button
                className="checkout-btn checkout-btn-primary"
                onClick={handleCheckout}
                disabled={loading || processing || cartCount === 0}
              >
                {loading || processing ? (
                  <>
                    <div className="checkout-spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} />
                    {processing && usesPaymentCheckout ? 'Redirecting to payment…' : 'Processing…'}
                  </>
                ) : (
                  <>
                    <CheckIcon />
                    {usesPaymentCheckout
                      ? `Proceed To Stripe (${cartCount} item${cartCount > 1 ? 's' : ''})`
                      : `Create Placements (${cartCount} item${cartCount > 1 ? 's' : ''})`}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CheckoutModal;
